<template>
    <div class="w-full md:w-4/5 m-auto py-6 px-0 md:px-12">
        <div v-if="!loading" class="lds-spinner">
            <div />
            <div />
            <div />
            <div />
            <div />
            <div />
            <div />
            <div />
            <div />
            <div />
            <div />
            <div />
        </div>
        <h1 class="mb-4 text-green-600 text-2xl text-left font-bold text-2xl">
            <router-link :to="{ name: 'ViewApplication' }">Applications</router-link>
            / {{ personalData.first_name }} {{ personalData.last_name }}
        </h1>
        <div class="bg-white bg-gray-100 border px-2 py-2 mt-1 pb-6">
            <div class="flex justify-between mb-6 px-8 mt-4 align-baseline text-sm">
                <div v-if="loading" class="w-full flex flex-col items-center">
                    <table class="table-auto text-left">
                        <tr>
                            <th class="text-right pr-4">Applicant:</th>
                            <td class="pr-8">{{ personalData.first_name }} {{ personalData.last_name }}</td>
                            <th class="text-right pr-4">Academic year:</th>
                            <td class="pr-8">{{ applicationData?.academicYear }}</td>
                        </tr>
                        <tr>
                            <th class="text-right pr-4">Programme type:</th>
                            <td class="pr-8">{{ applicationData?.programmeType }}</td>
                            <th class="text-right pr-4">Study mode:</th>
                            <td class="">{{ applicationData?.study_mode_title }}</td>
                        </tr>
                        <tr>
                            <th class="text-right pr-4">Programme:</th>
                            <td class="pr-8">{{ applicationData?.programmeName }}</td>
                            <th class="text-right pr-4">Status:</th>
                            <td class="">
                                <span>{{ applicationData.public_status }}</span>
                            </td>
                        </tr>
                    </table>

                    <div v-if="applicationData.is_part_of_group">
                        <p>*** This application is part of a group. ***</p>
                        <p class="font-bold">
                            The Programme displayed only reflects the primary application within this group. Once you submit
                            the application, only the primary application can be modified. Any other programme choices that you may make outside of the primary choice will be converted into a separate application and processed independently.
                        </p>
                    </div>
                </div>
            </div>
            <div id="programme_details"
                 class="bg-white border border-gray-300 cursor-pointer hover:bg-gray-200 flex justify-between px-8 mt-1"
                 @click="programmeDetailsShow = !programmeDetailsShow"
            >
                <h1 class="text-1xl leading-10">Programme Details</h1>
                <icon :name="!programmeDetailsShow ? 'cheveron-right' : 'cheveron-down'"
                      class="fill-current w-7 h-10 ml-2"
                />
            </div>
            <div id="programme_details_content" :class="{ hide: !programmeDetailsShow }"
                 class="bg-white border border-t-0 border-gray-300 overflow-hidden"
            >
                <programme-details v-if="loading"
                                   :programme-type="applicationData?.programme_code"
                                   :application-data="applicationDetails"
                                   :application-ref="$route.params.id"
                                   :mode-of-attendance="modeOfAttendance"
                                   :programme-data="programmeData.data"
                                   :programme-types-list="programmeTypesList"
                                   :programmes="programmesList['data']"
                                   :proposed-start-dates="proposedStartDates"
                                   :courses="coursesList?.data"
                                   @programmeChange="programmeChange"
                                   @typeChanged="programmeTypeChange"
                />
            </div>
            <div v-for="section in applicationData?.module.data.sections.data" :key="section.key">
                <div :id="`${section.key}`"
                     class="bg-white border border-gray-300 cursor-pointer hover:bg-gray-200 flex justify-between px-8 mt-1 text-left"
                     @click="sectionToggles[section.key] = !sectionToggles[section.key]"
                >
                    <h4 class="text-1xl leading-10 text-bold">{{ section.title }}</h4>
                    <icon :name="!sectionToggles[section.key] ? 'cheveron-right' : 'cheveron-down'"
                          class="fill-current w-7 h-10 ml-2"
                    />
                </div>
                <div :id="`${section.key}_content`" :class="{ hide: !sectionToggles[section.key] }" class="m-8 text-left">
                    <section-render v-if="loading" :application-id="applicationData.reference" :section="section" :section-data="applicationData.application_data" />
                </div>
            </div>
            <div id="documentation" class="bg-white border border-gray-300 cursor-pointer hover:bg-gray-200 flex justify-between px-8 mt-1"
                 @click="documentationShow = !documentationShow"
            >
                <h1 class="text-1xl leading-10">Supporting Documents</h1>
                <icon :name="!documentationShow ? 'cheveron-right' : 'cheveron-down'"
                      class="fill-current w-7 h-10 ml-2"
                />
            </div>
            <div id="documentation_content" :class="{ hide: !documentationShow }"
                 class="bg-white border border-t-0 border-gray-300 overflow-hidden flex flex-wrap"
            >
                <application-documentation v-if="loading" :application-ref="$route.params.id" :document-types="documentTypes" :documentation="documentation" />
            </div>
            <div id="declaration"
                 class="bg-white border border-gray-300 cursor-pointer hover:bg-gray-200 flex justify-between px-8 mt-1"
                 @click="declarationShow = !declarationShow"
            >
                <h4 class="text-1xl leading-10">Declaration & Submission</h4>
                <icon :name="!declarationShow ? 'cheveron-right' : 'cheveron-down'"
                      class="fill-current w-7 h-10 ml-2"
                />
            </div>
            <div id="declaration_content" :class="{ hide: !declarationShow }"
                 class="bg-white border border-t-0 border-gray-300 overflow-hidden flex flex-wrap"
            >
                <declaration v-if="loading"
                             :application-data="applicationData"
                             :application-reference="$route.params.id"
                             :contact-data="contactData"
                             :documentation="documentation"
                             :employment-history="employmentHistory"
                             :equal-opportunities="equalOpportunities"
                             :payment-data="paymentData"
                             :personal-data="personalData"
                             :prior-education="priorEducation"
                             :programme-choice="firstChoice"
                             :programme-choice-type="firstChoiceType"
                             :programme-data="programmeData.data"
                />
            </div>
        </div>
    </div>
</template>

<script>
import Icon from '@/shared/Icon'
import ProgrammeDetails from './Details/ProgrammeDetails'
import SectionRender from './Details/SectionRender'
import Declaration from './Details/Declaration'
import ApplicationDocumentation from '@/views/Application/Details/ApplicationDocumentation'

export default {
  name: 'EditModuleApplication',
  metaInfo() {
    return {
      title: 'Edit Application',
    }
  },
  components: {
    ProgrammeDetails,
    Icon,
    SectionRender,
    ApplicationDocumentation,
    Declaration,
  },
  data() {
    return {
      personalDetailsShow: false,
      nationalityShow: false,
      contactDetailsShow: false,
      statementShow: false,
      programmeDetailsShow: false,
      educationDetailsShow: false,
      employmentDetailsShow: false,
      fundingDetailsShow: false,
      referencesShow: false,
      documentationShow: false,
      equalOpportunitiesShow: false,
      declarationShow: true,
      applicationData: [],
      sectionToggles: [],
      personalData: {
        first_name: '',
        last_name: '',
      },
      contactData: [],
      priorEducation: [],
      employmentHistory: [],
      programmeData: [],
      documentation: [],
      documentTypes: [],
      programmes: [],
      sponsorData: [],
      paymentData: [],
      modeOfFunding: 'Applied',
      loading: false,
      genders: [],
      ethnicities: [],
      disabilities: [],
      equalOpportunities: [],
      religiousBelief: [],
      sexualOrientation: [],
      sourcesOfInterest: [],
      genderIdentity: [],
      modeOfAttendance: [],
      proposedStartDates: [],
      scholarships: [],
      firstChoiceTypeReference: '',
      firstChoiceReference: '',
      nationality: [],
    }
  },
  computed: {
    firstChoiceType: {
      get: function () {
        const programmeTypes = this.$store.getters.programmeTypesList

        if (programmeTypes !== undefined) {
          if (this.firstChoiceTypeReference !== undefined && this.firstChoiceTypeReference.length > 0) {
            return programmeTypes.data.filter(programmeType => programmeType['reference'] === this.firstChoiceTypeReference)[0]
          } else {
            if (programmeTypes.data.length > 0 && this.$store.getters.applicationDetails['data']['first_choice_type']) {
              return programmeTypes.data.filter(programmeType => programmeType['reference'] === this.$store.getters.applicationDetails['data']['first_choice_type'])[0]
            }
          }
        }
        return {name: '', num_references_required: 2}
      },
      set: function (newValue) {
        this.firstChoiceTypeReference = newValue
      },
    },
    firstChoice: {
      get: function () {
        const programmes = this.$store.getters.programmesList

        if (programmes !== undefined) {
          if (this.firstChoiceReference !== undefined && this.firstChoiceReference.length > 0) {
            return programmes.data.filter(programme => programme['reference'] === this.firstChoiceReference)[0]
          } else {
            if (programmes.data.length > 0 && this.$store.getters.applicationDetails['data']['first_choice'] !== null) {
              return programmes.data.filter(programme => programme['reference'] === this.$store.getters.applicationDetails['data']['first_choice'])[0]
            }
          }
        }
        return {name: '', programme_title: '', sits_ipo_ayrc: '', moa_name: ''}
      },
      set: function (newValue) {
        this.firstChoiceReference = newValue
      },
    },
    applicationDetails() {
      return this.$store.getters.applicationDetails
    },
    programmesList() {
      return this.$store.getters.programmesList
    },
    coursesList() {
      return this.$store.getters.coursesList
    },
    gendersList() {
      return this.$store.getters.gendersList
    },
    titlesList() {
      return this.$store.getters.titlesList
    },
    ethnicityList() {
      return this.$store.getters.ethnicityList
    },
    disabilityList() {
      return this.$store.getters.disabilityList
    },
    religiousBeliefList() {
      return this.$store.getters.religiousBeliefList
    },
    sexualOrientationList() {
      return this.$store.getters.sexualOrientationList
    },
    genderIdentityList() {
      return this.$store.getters.genderIdentityList
    },
    documentTypesList() {
      return this.$store.getters.documentTypesList
    },
    sourcesOfInterestList() {
      return this.$store.getters.sourcesOfInterestList
    },
    contactDetailTypesList() {
      return this.$store.getters.contactDetailTypesList
    },
    countriesOfDomicileList() {
      return this.$store.getters.countriesOfDomicileList
    },
    countriesOfBirthList() {
      return this.$store.getters.countriesOfBirthList
    },
    programmeTypesList() {
      return this.$store.getters.programmeTypesList
    },
    modeOfAttendanceList() {
      return this.$store.getters.modeOfAttendanceList
    },
    proposedStartDateList() {
      return this.$store.getters.proposedStartDateList
    },
    scholarshipsList() {
      return this.$store.getters.scholarshipsList
    },
    nationalityList() {
      return this.$store.getters.nationalityList
    },
  },
  watch: {
    applicationDetails(applicationData) {
      this.applicationData = applicationData.data
      this.personalData = applicationData.data.user.data
      this.contactData = applicationData.data.contactDetails.data
      this.programmeData = applicationData.data.applicationProgrammes
      this.priorEducation = applicationData.data.priorEducation.data
      this.employmentHistory = applicationData.data.employmentHistory.data
      this.equalOpportunities = applicationData.data.equalOpportunities.data
      this.documentation = applicationData.data.documents.data
      this.documentTypes = applicationData.data.documentTypes
      this.sponsorData = applicationData.data.applicationSponsors.data
      this.paymentData = applicationData.data.payments.data
      this.loading = true

      if (this.applicationData['status'] !== 'Draft') {
        window.location.href = '/applications/' + this.applicationData['reference'] + '/Review'
      }

    },
    programmesList(data) {
      this.programmes = data
    },
    gendersList(data) {
      this.genders = data
    },
    ethnicityList(data) {
      this.ethnicities = data
    },
    disabilityList(data) {
      this.disabilities = data
    },
    religiousBeliefList(data) {
      this.religiousBelief = data
    },
    sexualOrientationList(data) {
      this.sexualOrientation = data
    },
    genderIdentityList(data) {
      this.genderIdentity = data
    },
    documentTypesList(data) {
      this.documentTypes = data
    },
    sourcesOfInterestList(data) {
      this.sourcesOfInterest = data
    },
    modeOfAttendanceList(data) {
      this.modeOfAttendance = data
    },
    proposedStartDateList(data) {
      this.proposedStartDates = data
    },
    scholarshipsList(data) {
      this.scholarships = data
    },
    nationalityList(data) {
      this.nationality = data
    },
  },
  mounted() {
  },
  created() {
    this.loadApplication()

    // pass parameter in url to expand that section on load (eg section=educationDetailsShow). then remove the param
    const expandSection = new URLSearchParams(window.location.search).get('section')
    this[expandSection] = true

    const url = new URL(location)
    url.searchParams.delete('section')
    history.replaceState(null, null, url)
  },
  methods: {
    loadApplication() {
      this.$store.dispatch('retrieveSingleApplication', {
        applicationRef: this.$route.params.id,
      })
    },
    programmeTypeChange(newValue) {
      this.firstChoiceType = newValue
    },
    programmeChange(newValue) {
      this.firstChoice = newValue
    },
    modeOfFundingChange(value) {
      this.modeOfFunding = value
    },
  },
}
</script>
<style scoped>
.hide {
  display: none !important;
}
</style>